import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, CalendarDays, Check, Lock, ChevronRight, ChevronLeft, ChevronsRight, ChevronsLeft, ArrowLeft, RotateCcw } from "lucide-react";

import SedecShell from "@/components/layout/SedecShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { usePaginationState } from "@/lib/use-paginated-query";
import { PaginationControls } from "@/components/PaginationControls";
import {
  listAnnees, createAnnee, activerAnnee, cloturerAnnee, rouvrirAnnee,
  getAffectation, affecterEcoles, retirerEcoles,
  affecterTousEcoles, retirerTousEcoles,
  type Annee, type EcoleAffectation,
} from "@/lib/api";
import { friendlyError } from "@/lib/errors";

const STATUT_LABEL: Record<string, string> = {
  A_VENIR: "À venir", EN_COURS: "En cours", CLOTUREE: "Clôturée",
};

export default function SedecAnnees() {
  const [selected, setSelected] = useState<Annee | null>(null);
  if (selected) {
    return <AffectationScreen annee={selected} onBack={() => setSelected(null)} />;
  }
  return <AnneesListScreen onAffecter={setSelected} />;
}

function AnneesListScreen({ onAffecter }: { onAffecter: (a: Annee) => void }) {
  const qc = useQueryClient();
  const { page, size, setPage, setSize, resetToFirstPage } = usePaginationState(20);
  const [q, setQ] = useState("");
  const [statut, setStatut] = useState("");
  const [actifOnly, setActifOnly] = useState(false);
  const [libelle, setLibelle] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [confirmActivate, setConfirmActivate] = useState<Annee | null>(null);
  // 21/10/2026 (audit école FA-13, SEDEC-08) : « Clôturer » partait en un clic
  // et pouvait laisser tout le réseau sans année. Confirmation par la saisie
  // du libellé ; l'année active ne se clôture pas (le serveur refuse : on
  // active la suivante).
  const [confirmCloture, setConfirmCloture] = useState<Annee | null>(null);
  const [saisieCloture, setSaisieCloture] = useState("");
  const libelleCloture = confirmCloture ? confirmCloture.libelle : "";
  // 22/10/2026 (relecture) : activer bascule tout le réseau, clôture l'année
  // en cours et inscrit les préinscrits : la saisie du libellé est demandée
  // aussi. On n'active ni ne rouvre une année passée (le serveur refuse) ;
  // une année future clôturée par erreur se « rouvre » sans être activée.
  const [saisieActivation, setSaisieActivation] = useState("");
  const libelleActivation = confirmActivate ? confirmActivate.libelle : "";

  const onChangeQ = (v: string) => { setQ(v); resetToFirstPage(); };
  const onChangeStatut = (v: string) => { setStatut(v); resetToFirstPage(); };
  const onToggleActif = (c: boolean) => { setActifOnly(c); resetToFirstPage(); };

  const { data, isLoading } = useQuery({
    queryKey: ["annees-globales", { q, statut, actifOnly, page, size }],
    queryFn: () => listAnnees({ q, statut, actifOnly, page, size }),
  });
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const invalider = () => qc.invalidateQueries({ queryKey: ["annees-globales"] });

  // Année active du réseau, lue à part : la liste ci-dessous est filtrée et paginée.
  const { data: activeData } = useQuery({
    queryKey: ["annees-globales", "active"],
    queryFn: () => listAnnees({ actifOnly: true, page: 0, size: 1 }),
  });
  const active = activeData?.items?.find((a) => a.estActive) ?? null;
  const aujourdhui = new Date().toISOString().slice(0, 10);
  /** Vient après l'année active et n'est pas terminée : seule une telle année s'active ou se rouvre. */
  const apresActive = (a: Annee) =>
    a.dateFin >= aujourdhui && (!active || a.dateDebut > active.dateDebut);

  const creer = useMutation({
    mutationFn: () => createAnnee({ libelle: libelle.trim(), dateDebut, dateFin }),
    onSuccess: () => { toast.success("Année créée."); setLibelle(""); setDateDebut(""); setDateFin(""); invalider(); },
    onError: (e: unknown) => toast.error(friendlyError(e)),
  });
  const activer = useMutation({
    mutationFn: (id: string) => activerAnnee(id),
    onSuccess: () => { toast.success("Année activée (toutes les écoles affectées)."); invalider(); },
    onError: (e: unknown) => toast.error(friendlyError(e)),
  });
  const cloturer = useMutation({
    mutationFn: (id: string) => cloturerAnnee(id),
    onSuccess: () => { toast.success("Année clôturée."); invalider(); },
    onError: (e: unknown) => toast.error(friendlyError(e)),
  });
  const rouvrir = useMutation({
    mutationFn: (id: string) => rouvrirAnnee(id),
    onSuccess: () => { toast.success("Année rouverte : elle est de nouveau « À venir », l'année active ne change pas."); invalider(); },
    onError: (e: unknown) => toast.error(friendlyError(e)),
  });

  const canCreate = libelle.trim().length > 0 && dateDebut !== "" && dateFin !== "";

  return (
    <SedecShell>
      <div className="space-y-5">
        <header>
          <div className="text-xs uppercase tracking-wider text-slate-500">Gestion des années scolaires</div>
          <h1 className="text-2xl font-bold text-navy">Années scolaires</h1>
          <p className="text-sm text-slate-600">Créez une année globale, activez-la pour toutes les écoles affectées, puis gérez l'affectation des écoles.</p>
        </header>

        <Card className="p-4 border border-slate-200 shadow-none">
          <h2 className="font-semibold text-navy mb-3">Créer une année</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Libellé</label>
              <Input value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="2026-2027" data-testid="input-libelle" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Date de début</label>
              <Input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} data-testid="input-date-debut" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Date de fin</label>
              <Input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} data-testid="input-date-fin" />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button type="button" disabled={!canCreate || creer.isPending} onClick={() => creer.mutate()} data-testid="btn-creer">
              {creer.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              <span className="ml-1">Créer une année</span>
            </Button>
          </div>
        </Card>

        <div className="flex items-end gap-3 flex-wrap">
          <div className="w-64">
            <label className="block text-xs text-slate-500 mb-1">Rechercher</label>
            <Input value={q} onChange={(e) => onChangeQ(e.target.value)} placeholder="Libellé (ex. 2026)" data-testid="filtre-q" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Statut</label>
            <select
              value={statut}
              onChange={(e) => onChangeStatut(e.target.value)}
              data-testid="filtre-statut"
              className="border border-slate-200 rounded h-9 px-2 text-sm bg-white"
            >
              <option value="">Tous</option>
              <option value="A_VENIR">À venir</option>
              <option value="EN_COURS">En cours</option>
              <option value="CLOTUREE">Clôturée</option>
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 h-9">
            <input type="checkbox" checked={actifOnly} onChange={(e) => onToggleActif(e.target.checked)} data-testid="filtre-actif" />
            Actives uniquement
          </label>
        </div>

        <Card className="p-0 border border-slate-200 shadow-none overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-slate-500"><CalendarDays className="w-8 h-8 mx-auto mb-2 text-slate-300" />Aucune année. Créez-en une ci-dessus.</div>
          ) : (
            <>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2.5">Libellé</th>
                  <th className="text-left">Période</th>
                  <th className="text-left">Statut</th>
                  <th className="text-center">Écoles</th>
                  <th className="text-center pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const cloturee = a.statut === "CLOTUREE";
                  return (
                    <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-annee-${a.id}`}>
                      <td className="px-4 py-2 font-medium text-navy">{a.libelle}</td>
                      <td className="text-xs text-slate-600">{a.dateDebut} → {a.dateFin}</td>
                      <td>
                        <span className="text-xs text-slate-600">{STATUT_LABEL[a.statut] ?? a.statut}</span>
                        {a.estActive ? <span className="ml-2 text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" title="Pour la clôturer, activez l'année suivante." data-testid={`badge-active-${a.id}`}>active</span> : null}
                      </td>
                      <td className="text-center text-xs text-slate-600">{a.nbEcoles}</td>
                      <td className="text-center pr-4">
                        <div className="inline-flex gap-2">
                          <button type="button" onClick={() => onAffecter(a)} className="text-xs px-2.5 py-1 border rounded inline-flex items-center gap-1 border-navy/20 text-navy hover:bg-slate-50" data-testid={`affecter-${a.id}`}>
                            Affecter des écoles
                          </button>
                          {!a.estActive && !cloturee && apresActive(a) ? (
                            <button type="button" onClick={() => { setSaisieActivation(""); setConfirmActivate(a); }} disabled={activer.isPending} className="text-xs px-2.5 py-1 border rounded inline-flex items-center gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`activer-${a.id}`}>
                              <Check className="w-3 h-3" /> Activer
                            </button>
                          ) : null}
                          {cloturee && apresActive(a) ? (
                            <button type="button" onClick={() => rouvrir.mutate(a.id)} disabled={rouvrir.isPending} title="Remet l'année « À venir » sans l'activer." className="text-xs px-2.5 py-1 border rounded inline-flex items-center gap-1 border-navy/20 text-navy hover:bg-slate-50" data-testid={`rouvrir-${a.id}`}>
                              <RotateCcw className="w-3 h-3" /> Rouvrir
                            </button>
                          ) : null}
                          {!cloturee && !a.estActive ? (
                            <button type="button" onClick={() => { setSaisieCloture(""); setConfirmCloture(a); }} disabled={cloturer.isPending} className="text-xs px-2.5 py-1 border rounded inline-flex items-center gap-1 border-red-200 text-red-700 hover:bg-red-50" data-testid={`cloturer-${a.id}`}>
                              <Lock className="w-3 h-3" /> Clôturer
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            <PaginationControls page={page} size={size} total={total} onPageChange={setPage} onSizeChange={setSize} />
            </>
          )}
        </Card>
      </div>

      <Dialog
        open={confirmActivate != null}
        onOpenChange={(o) => {
          /* v8 ignore start -- sans DialogTrigger, Radix n'appelle onOpenChange qu'avec o=false (Échap/overlay) ; la branche o=true est inatteignable */
          if (!o) setConfirmActivate(null);
          /* v8 ignore stop */
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader className="-mx-6 -mt-6 rounded-t-lg bg-navy px-6 py-4 text-left">
            <DialogTitle className="font-display text-lg font-bold text-white">Activer l'année</DialogTitle>
            <DialogDescription className="text-white/70">Cette action est globale et clôture l'année en cours.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              Activer <span className="font-semibold text-navy">{confirmActivate?.libelle}</span> la rendra l'année courante de <span className="font-semibold">toutes les écoles affectées</span> et <span className="font-semibold">clôturera l'année en cours</span>. Confirmer ?
            </p>
            <p className="text-sm text-slate-700">
              Pour confirmer, tapez le libellé de l'année : <span className="font-semibold text-navy">{libelleActivation}</span>
            </p>
            <Input value={saisieActivation} onChange={(e) => setSaisieActivation(e.target.value)} placeholder={libelleActivation} data-testid="saisie-activation" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmActivate(null)}>Annuler</Button>
              <Button
                onClick={() => {
                  /* v8 ignore start -- confirmActivate non nul tant que le dialogue est ouvert (bouton rendu) */
                  if (confirmActivate) activer.mutate(confirmActivate.id);
                  /* v8 ignore stop */
                  setConfirmActivate(null);
                }}
                disabled={saisieActivation.trim() !== libelleActivation || activer.isPending}
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="btn-confirm-activate"
              >
                {activer.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                <span className="ml-1">Activer</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmCloture != null}
        onOpenChange={(o) => {
          /* v8 ignore start -- sans DialogTrigger, Radix n'appelle onOpenChange qu'avec o=false (Échap/overlay) ; la branche o=true est inatteignable */
          if (!o) setConfirmCloture(null);
          /* v8 ignore stop */
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader className="-mx-6 -mt-6 rounded-t-lg bg-red-700 px-6 py-4 text-left">
            <DialogTitle className="font-display text-lg font-bold text-white">Clôturer l'année</DialogTitle>
            <DialogDescription className="text-white/80">Une année clôturée n'est plus proposée aux écoles.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              Pour confirmer, tapez le libellé de l'année : <span className="font-semibold text-navy">{libelleCloture}</span>
            </p>
            <Input value={saisieCloture} onChange={(e) => setSaisieCloture(e.target.value)} placeholder={libelleCloture} data-testid="saisie-cloture" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmCloture(null)}>Annuler</Button>
              <Button
                onClick={() => {
                  /* v8 ignore start -- confirmCloture non nul tant que le dialogue est ouvert (bouton rendu) */
                  if (confirmCloture) cloturer.mutate(confirmCloture.id);
                  /* v8 ignore stop */
                  setConfirmCloture(null);
                }}
                disabled={saisieCloture.trim() !== libelleCloture || cloturer.isPending}
                className="bg-red-700 text-white hover:bg-red-800"
                data-testid="btn-confirm-cloture"
              >
                <Lock className="w-4 h-4" />
                <span className="ml-1">Clôturer</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </SedecShell>
  );
}

function AffectationScreen({ annee, onBack }: { annee: Annee; onBack: () => void }) {
  const qc = useQueryClient();
  const [qDispo, setQDispo] = useState("");
  const [qAff, setQAff] = useState("");
  const [selDispo, setSelDispo] = useState<Set<string>>(new Set());
  const [selAff, setSelAff] = useState<Set<string>>(new Set());

  const dispo = useQuery({
    queryKey: ["affectation", annee.id, "disponibles", qDispo],
    queryFn: () => getAffectation(annee.id, { cote: "disponibles", q: qDispo, size: 50 }),
  });
  const aff = useQuery({
    queryKey: ["affectation", annee.id, "affectees", qAff],
    queryFn: () => getAffectation(annee.id, { cote: "affectees", q: qAff, size: 50 }),
  });
  const invalider = () => qc.invalidateQueries({ queryKey: ["affectation", annee.id] });

  const affecter = useMutation({
    mutationFn: (ids: string[]) => affecterEcoles(annee.id, ids),
    onSuccess: (r) => { toast.success(`${r.affectees} école(s) affectée(s).`); setSelDispo(new Set()); invalider(); },
    onError: (e: unknown) => toast.error(friendlyError(e)),
  });
  const retirer = useMutation({
    mutationFn: (ids: string[]) => retirerEcoles(annee.id, ids),
    onSuccess: (r) => {
      if (r.retirees.length) toast.success(`${r.retirees.length} école(s) retirée(s).`);
      r.bloquees.forEach((b) => toast.error(`${b.denomination} : ${b.raison}`));
      setSelAff(new Set()); invalider();
    },
    onError: (e: unknown) => toast.error(friendlyError(e)),
  });

  const affecterTous = useMutation({
    mutationFn: (q: string) => affecterTousEcoles(annee.id, q),
    onSuccess: (r) => { toast.success(`${r.affectees} école(s) affectée(s).`); setSelDispo(new Set()); invalider(); },
    onError: (e: unknown) => toast.error(friendlyError(e)),
  });
  const retirerTous = useMutation({
    mutationFn: (q: string) => retirerTousEcoles(annee.id, q),
    onSuccess: (r) => {
      if (r.retirees.length) toast.success(`${r.retirees.length} école(s) retirée(s).`);
      r.bloquees.forEach((b) => toast.error(`${b.denomination} : ${b.raison}`));
      setSelAff(new Set()); invalider();
    },
    onError: (e: unknown) => toast.error(friendlyError(e)),
  });

  const dispoItems = dispo.data?.items ?? [];
  const affItems = aff.data?.items ?? [];
  const dispoTotal = dispo.data?.total ?? 0;
  const affTotal = aff.data?.total ?? 0;

  return (
    <SedecShell>
      <div className="space-y-4">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-navy" data-testid="btn-retour">
          <ArrowLeft className="w-4 h-4" /> Retour aux années
        </button>
        <header>
          <h1 className="text-2xl font-bold text-navy">Affectation · {annee.libelle}</h1>
          <p className="text-sm text-slate-600">Transférez les écoles entre « disponibles » et « affectées ». Le retrait est refusé si l'école a déjà des opérations sur cette année.</p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
          <Column title="Écoles non affectées" q={qDispo} setQ={setQDispo}
            items={dispoItems} loading={dispo.isLoading} sel={selDispo} setSel={setSelDispo}
            total={dispo.data?.total ?? 0} testid="col-dispo" />

          <div className="flex flex-row md:flex-col justify-center gap-2">
            <IconBtn onClick={() => affecter.mutate(Array.from(selDispo))} disabled={selDispo.size === 0 || affecter.isPending} testid="btn-affecter" title="Affecter la sélection"><ChevronRight className="w-4 h-4" /></IconBtn>
            <IconBtn onClick={() => affecterTous.mutate(qDispo)} disabled={dispoTotal === 0 || affecterTous.isPending} testid="btn-affecter-tout" title="Affecter tout (filtré)"><ChevronsRight className="w-4 h-4" /></IconBtn>
            <IconBtn onClick={() => retirer.mutate(Array.from(selAff))} disabled={selAff.size === 0 || retirer.isPending} testid="btn-retirer" title="Retirer la sélection"><ChevronLeft className="w-4 h-4" /></IconBtn>
            <IconBtn onClick={() => retirerTous.mutate(qAff)} disabled={affTotal === 0 || retirerTous.isPending} testid="btn-retirer-tout" title="Retirer tout (filtré)"><ChevronsLeft className="w-4 h-4" /></IconBtn>
          </div>

          <Column title="Écoles affectées" q={qAff} setQ={setQAff}
            items={affItems} loading={aff.isLoading} sel={selAff} setSel={setSelAff}
            total={aff.data?.total ?? 0} testid="col-aff" />
        </div>
      </div>
    </SedecShell>
  );
}

function Column({ title, q, setQ, items, loading, sel, setSel, total, testid }: {
  title: string; q: string; setQ: (v: string) => void; items: EcoleAffectation[];
  loading: boolean; sel: Set<string>; setSel: (s: Set<string>) => void; total: number; testid: string;
}) {
  const toggle = (id: string) => {
    const next = new Set(sel);
    next.has(id) ? next.delete(id) : next.add(id);
    setSel(next);
  };
  return (
    <Card className="p-3 border border-slate-200 shadow-none flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-navy text-sm">{title}</h3>
        <span className="text-xs text-slate-500">{total}</span>
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer…" className="mb-2" data-testid={`${testid}-filtre`} />
      <div className="flex-1 overflow-y-auto max-h-[60vh] border border-slate-100 rounded">
        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-navy" /></div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-xs text-slate-400">Aucune école.</div>
        ) : items.map((e) => (
          <label key={e.ecoleId} className="flex items-center gap-2 px-2 py-1.5 text-sm border-b border-slate-50 hover:bg-slate-50 cursor-pointer" data-testid={`${testid}-row-${e.ecoleId}`}>
            <input type="checkbox" checked={sel.has(e.ecoleId)} onChange={() => toggle(e.ecoleId)} />
            <span className="text-navy">{e.denomination}</span>
            {e.codeEtablissementSedec ? <span className="text-xs text-slate-400">· {e.codeEtablissementSedec}</span> : null}
          </label>
        ))}
      </div>
    </Card>
  );
}

function IconBtn({ children, onClick, disabled, testid, title }: {
  children: React.ReactNode; onClick: () => void; disabled: boolean; testid: string; title: string;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} data-testid={testid}
      className="w-9 h-9 inline-flex items-center justify-center border rounded border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
      {children}
    </button>
  );
}
